import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { PulseTrafficKpi } from "./types";

export function PulseKpiCard({ kpi }: { kpi: PulseTrafficKpi }) {
  const delta = kpi.deltaPct;
  const trend = delta === null ? "flat" : delta > 0.5 ? "up" : delta < -0.5 ? "down" : "flat";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;

  return (
    <div className="nx-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="nx-eyebrow">{kpi.label}</span>
        {delta !== null && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-2xs font-semibold px-1.5 py-0.5 rounded",
              trend === "up" && "bg-success-soft text-success",
              trend === "down" && "bg-danger-soft text-danger",
              trend === "flat" && "bg-bg-subtle text-fg-muted",
            )}
          >
            <TrendIcon size={10} />
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-3xl font-bold text-fg tracking-tight tabular-nums">
          {formatNumber(kpi.value, kpi.unit)}
        </span>
        {kpi.unit && kpi.unit !== "CLP" && (
          <span className="text-xs text-fg-subtle">{kpi.unit}</span>
        )}
      </div>
      <Sparkline points={kpi.spark.map((p) => p.value)} trend={trend} />
    </div>
  );
}

function formatNumber(n: number, unit?: string | null): string {
  if (unit === "CLP") {
    return "$" + new Intl.NumberFormat("es-CL").format(Math.round(n));
  }
  return new Intl.NumberFormat("es-CL").format(n);
}

function Sparkline({ points, trend }: { points: number[]; trend: "up" | "down" | "flat" }) {
  if (points.length < 2) {
    return <div className="h-8 bg-bg-subtle rounded" />;
  }
  const max = Math.max(...points, 1);
  const min = Math.min(...points);
  const range = max - min || 1;
  const w = 100;
  const h = 32;
  const step = w / (points.length - 1);
  const path = points
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  const color =
    trend === "up"
      ? "var(--nx-success)"
      : trend === "down"
      ? "var(--nx-danger)"
      : "var(--nx-fg-subtle)";

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full h-8"
      aria-hidden
    >
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  );
}
